import React, { useEffect, useState } from 'react';
import { UncontrolledDropdown } from 'reactstrap';
import {
  Text,
  WarningOctagonIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  StyledDropdownMenu,
  StyledDropdownItem,
} from '@aag-development/becreatives-ui';
import {
  RevisionCursorNavLink,
  RevisionFallbackImage,
  RevisionFallbackWrapper,
  RevisionVersionControlButton,
  RevisionMainContentCard,
  RevisionVideoControlRow,
} from './RevisionsContentMain.styled';
import { If } from 'src/components/If';
import { RevisionVersionTooltipPopover } from './RevisionVersionTooltipPopover';
import { useTranslation } from 'react-i18next';
import { ShareMediaButton, ContentGenerationButtons } from 'src/components/Button';
import { useRequestId } from 'src/features/requests/use-request-id';
import { MediaViewer } from 'src/components/MediaCard';
import { getPreferredSource, makeSourceOptions, useMediaDownload } from 'src/features/media';
import RevisionFileDisplayFallback from 'src/assets/images/revision-file-display-fallback.png';
import { RevisionMediaList } from 'src/features/revisions';
import { cn } from 'src/lib/utils';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { CardErrorFallback } from 'src/features/fallback';
import { Skeleton } from 'src/components/ui/skeleton';
import { MediaSignedUrlsQueryProvider } from 'src/features/media/use-media-signed-urls-query-context';
import { RevisionVideoPlayer } from 'src/features/revisions/revision-video-player';
import { AspectRatio } from 'src/components/ui/aspect-ratio';
import { useRequestRevisionsStore } from 'src/features/revisions/use-revisions-context-provider';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { RequestCommentsQueryContextProvider } from 'src/features/comments/use-request-comments';
import { EmptySpaceFallbackImage } from 'src/features/fallback/ui';
import {
  VideoPlayerContextProvider,
  VideoPlayerSkeleton,
} from 'src/features/video-player/video-player';
import { rateRevisionDialogPortalTarget } from 'src/features/revisions/rate-revision-composed-dialog';
import {
  RevisionChatContent,
  RevisionChatSection,
} from 'src/pages/RevisionsPage/revisions-content-main/revision-chat-content';
import { ChevronDown, CloudDownload, Save, CheckCircle } from 'lucide-react';
import {
  ArchivedMediaMessage,
  ReadyToDownloadMediaMessage,
  UnarchivedMediaMessage,
} from 'src/features/video-player/archived-media';
import {
  isMediaBeingUnarchived,
  isMediaNeedsUnarchiving,
  isMediaReadyForDownload,
} from './media-restoration.utils';
import { useSaveToMediaLibrary } from 'src/features/revisions/useSaveToMediaLibrary';
import { ButtonLoader } from 'src/components/ui/loader';

const RevisionsContentMain: React.FC = () => {
  const { t } = useTranslation('pages/shared');
  const requestId = useRequestId();

  const request = useDecoratedRequestContext();

  const {
    selectedRevision,
    selectedRevisionMedia,
    revisions,
    videoIsArchived,
    select,
    selectPrevious,
    selectNext,
    canSelectNext,
    canSelectPrevious,
  } = useRequestRevisionsStore();
  const { downloadAll, isDownloading } = useMediaDownload();
  const { saveToMediaLibrary, isSaving, isAlreadySaved, isAlreadySavedSync } = useSaveToMediaLibrary();
  const [currentFileAlreadySaved, setCurrentFileAlreadySaved] = useState(false);

  // Check if current file is already saved whenever selectedRevisionMedia changes
  useEffect(() => {
    if (selectedRevisionMedia?.index_url) {
      // Simple sync check - no API calls to avoid errors
      const syncStatus = isAlreadySavedSync(selectedRevisionMedia);
      setCurrentFileAlreadySaved(syncStatus);
    } else {
      setCurrentFileAlreadySaved(false);
    }
  }, [selectedRevisionMedia?.id, selectedRevisionMedia?.file_name, isAlreadySavedSync]);

  return (
    <div className={'tw-mb-4 tw-grid tw-gap-4 lg:tw-grid-cols-12'}>
      <RevisionMainContentCard
        id={rateRevisionDialogPortalTarget}
        color={'white'}
        className={'tw-mb-0 tw-self-start lg:tw-col-span-7 xl:tw-col-span-8'}
      >
        {/*Content top*/}
        <RevisionVideoControlRow className={'tw-flex tw-w-full tw-flex-col tw-gap-2 xl:tw-flex-row'}>
          <div className={'me-auto tw-flex tw-items-center'}>
            <RevisionVersionTooltipPopover placement={'bottom'} />
            <UncontrolledDropdown className={'tw-flex tw-min-w-28 tw-rounded xl:tw-h-auto xl:tw-flex-col'}>
              <RevisionVersionControlButton tag={'span'}>
                {selectedRevision?.title || t('unset', { ns: 'common' })}
                <ChevronDown size={14} className={'ms-2'} />
              </RevisionVersionControlButton>
              <StyledDropdownMenu>
                {/* Create a copy & reverse to sort by desc */}
                {[...revisions].reverse().map((revision, i) => (
                  <StyledDropdownItem
                    key={i}
                    className={cn({
                      'active text-primary': revision.title === selectedRevision?.title,
                    })}
                    onClick={() => {
                      if (selectedRevision?.raw.id !== revision.raw.id) {
                        select(revision);
                      }
                    }}
                  >
                    {revision.title}
                  </StyledDropdownItem>
                ))}
              </StyledDropdownMenu>
            </UncontrolledDropdown>
          </div>

          <If when={!!selectedRevisionMedia}>
            <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
              <ContentGenerationButtons
                mediaId={selectedRevisionMedia?.id ?? ''}
                requestId={requestId}
                className="tw-flex tw-flex-wrap tw-gap-1 sm:tw-gap-2"
              />
              {/* Disabled for now, because it not supposed to be visible at this stage of development */}
              {/* <ShareMediaButton
                mediaId={selectedRevisionMedia?.id ?? ''}
                requestId={requestId}
                className={'tw-text-primary'}
                title={'Share'}
              /> */}
            </div>
          </If>
        </RevisionVideoControlRow>

        {/*Content main*/}
        <If
          when={!selectedRevision?.isFirstContact}
          else={
            <RevisionFallbackWrapper>
              <RevisionFallbackImage src={RevisionFileDisplayFallback} />
              <Text color={'gray'} $size={'md'} $md={'xl'} $centeredContent={true}>
                <WarningOctagonIcon className={'me-2'} />
                Create a video revision to add files
              </Text>
            </RevisionFallbackWrapper>
          }
        >
          {/* Revision is not first contact, render video player with selected revision media list */}
          <div className={'tw-flex tw-w-full tw-flex-col tw-gap-2 xl:tw-flex-row'}>
            <RevisionMediaList />

            <If
              when={selectedRevision?.hasMedia}
              else={
                <AspectRatio
                  ratio={16 / 9}
                  className={
                    'tw-flex tw-h-full tw-w-full tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-self-start'
                  }
                >
                  <RevisionFallbackImage src={RevisionFileDisplayFallback} />
                  <Text color={'gray'} $size={'md'} $md={'xl'} $centeredContent={true}>
                    <WarningOctagonIcon className={'me-2'} />
                    This revision has no files
                  </Text>
                </AspectRatio>
              }
            >
              {/*If media can't be played, render generic media viewed*/}
              <If
                when={selectedRevisionMedia?.canPlay}
                else={
                  <AspectRatio ratio={16 / 9}>
                    <MediaViewer
                      renderOriginal={true}
                      media={selectedRevisionMedia!}
                      className={
                        'tw-mx-auto !tw-h-full !tw-w-auto !tw-rounded-md !tw-object-contain'
                      }
                    />
                  </AspectRatio>
                }
              >
                <div className={'tw-grow'}>
                  {/* Handle different media restoration states */}
                  {isMediaReadyForDownload(selectedRevisionMedia) ? (
                    <ReadyToDownloadMediaMessage
                      downloadUrl={selectedRevisionMedia!.restoration_download_url!}
                      expiryDate={selectedRevisionMedia!.restoration_expiry!}
                    />
                  ) : isMediaBeingUnarchived(selectedRevisionMedia) ? (
                    <UnarchivedMediaMessage />
                  ) : isMediaNeedsUnarchiving(selectedRevisionMedia, videoIsArchived) ? (
                    <ArchivedMediaMessage
                      mediaId={selectedRevisionMedia!.id}
                      requestId={request.id}
                    />
                  ) : (
                    <QueryErrorResetBoundary>
                      {({ reset }) => (
                        <ErrorBoundary
                          fallbackRender={({ error, resetErrorBoundary }: any) => (
                            <CardErrorFallback
                              error={error}
                              resetErrorBoundary={resetErrorBoundary}
                            />
                          )}
                          onReset={reset}
                        >
                          <React.Suspense fallback={<VideoPlayerSkeleton />}>
                            <MediaSignedUrlsQueryProvider id={selectedRevisionMedia!.id}>
                              <VideoPlayerContextProvider
                                meta={{
                                  fps: selectedRevisionMedia?.fps,
                                  durationInSeconds: selectedRevisionMedia?.durationInSeconds,
                                }}
                                defaultSrc={getPreferredSource(selectedRevisionMedia!)}
                                srcOptions={makeSourceOptions(selectedRevisionMedia!)}
                              >
                                <RevisionVideoPlayer />
                              </VideoPlayerContextProvider>
                            </MediaSignedUrlsQueryProvider>
                          </React.Suspense>
                        </ErrorBoundary>
                      )}
                    </QueryErrorResetBoundary>
                  )}
                </div>
              </If>
            </If>
          </div>
        </If>

        {/*Content bottom*/}
        <RevisionVideoControlRow className={'my-2'}>
          <RevisionCursorNavLink
            color={'primary'}
            disabled={!canSelectPrevious}
            onClick={selectPrevious}
          >
            <ChevronLeftIcon color={'primary'} className={'me-2'} />
            {t('revisions.revision.action.prev')}
          </RevisionCursorNavLink>

          <div className={'tw-flex tw-items-center tw-gap-2'}>
            {/* Save to Media Library Button */}
            <If when={!!selectedRevisionMedia?.index_url}>
              <RevisionVersionControlButton
                className={cn(
                  'me-2',
                  currentFileAlreadySaved && 'tw-opacity-60'
                )}
                tag={'span'}
                disabled={isSaving || !selectedRevisionMedia || currentFileAlreadySaved}
                onClick={async () => {
                  if (selectedRevisionMedia && !currentFileAlreadySaved) {
                    try {
                      await saveToMediaLibrary(selectedRevisionMedia);
                      // Immediately mark as saved to prevent duplicate clicks
                      setCurrentFileAlreadySaved(true);
                    } catch (error) {
                      console.error('Error saving to media library:', error);
                      // If save failed, keep it as not saved
                      setCurrentFileAlreadySaved(false);
                    }
                  }
                }}
              >
                {currentFileAlreadySaved ? (
                  <CheckCircle size={14} className={'tw-me-2'} />
                ) : (
                  <Save size={14} className={'tw-me-2'} />
                )}
                {currentFileAlreadySaved
                  ? t('Already Saved')
                  : t('Save to Media Library')
                }
                {isSaving && <ButtonLoader loading={isSaving} />}
              </RevisionVersionControlButton>
            </If>

            {/* Download Dropdown */}
            <UncontrolledDropdown>
              <RevisionVersionControlButton className={'ms-auto me-4'} tag={'span'}>
                <CloudDownload size={14} className={'tw-me-2'} />
                {t('Download files')}
              </RevisionVersionControlButton>
              <StyledDropdownMenu>
                <StyledDropdownItem
                  onClick={() => {
                    downloadAll([selectedRevisionMedia!]);
                  }}
                  disabled={!selectedRevisionMedia || isDownloading}
                >
                  <CloudDownload size={12} className={'tw-me-2'} />
                  {selectedRevisionMedia?.file_name ?? '-'}
                </StyledDropdownItem>
                <StyledDropdownItem
                  onClick={() => {
                    // Simplify using a single mapping instead of filtering multiple times with different collection_name values
                    const allCollectionMedia = selectedRevision?.media
                      .map((m) => ({ ...m, collection_name: m.collection_name }))
                      .filter((m) =>
                        [
                          'default',
                          'resizes',
                          'thumbnails',
                          'captions',
                          'project_files',
                          'transcript',
                        ].includes(m.collection_name),
                      );
                    downloadAll(allCollectionMedia ?? []);
                  }}
                  disabled={!selectedRevision?.hasMedia}
                >
                  <CloudDownload size={12} className={'tw-me-2'} />
                  {t('All current version files')}
                </StyledDropdownItem>
              </StyledDropdownMenu>
            </UncontrolledDropdown>
          </div>


          <RevisionCursorNavLink color={'primary'} disabled={!canSelectNext} onClick={selectNext}>
            {t('revisions.revision.action.next')}
            <ChevronRightIcon color={'primary'} className={'ms-2'} />
          </RevisionCursorNavLink>
        </RevisionVideoControlRow>
      </RevisionMainContentCard>

      {/* Request revision chat */}
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }: any) => (
              <RevisionChatSection className={'lg:tw-col-span-5 xl:tw-col-span-4'}>
                <EmptySpaceFallbackImage className={'tw-mt-auto tw-self-center'} />
                <CardErrorFallback
                  className={'tw-mt-auto tw-w-full tw-border-none tw-shadow-none'}
                  error={error}
                  resetErrorBoundary={resetErrorBoundary}
                  description={t('alerts:error.failed_to_load_comments', {
                    defaultValue: 'Failed to load comments',
                  })}
                />
              </RevisionChatSection>
            )}
            onReset={reset}
          >
            <React.Suspense
              fallback={
                <Skeleton className={'tw-h-44 lg:tw-col-span-5 lg:tw-h-auto xl:tw-col-span-4'} />
              }
            >
              <RequestCommentsQueryContextProvider
                request={request.id}
                filters={{
                  limit: 50,
                  from_revision: selectedRevision?.id,
                }}
              >
                <RevisionChatSection className={'lg:tw-col-span-5 xl:tw-col-span-4'}>
                  <RevisionChatContent />
                </RevisionChatSection>
              </RequestCommentsQueryContextProvider>
            </React.Suspense>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    </div>
  );
};

export { RevisionsContentMain };
